import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { 
  ArrowLeft, Lock, Bell, Mail, Shield, Save, KeyRound
} from "lucide-react";
import api from "../lib/api";

export default function SettingsPage() {
  const { trainer } = useAuth();
  const navigate = useNavigate();
  const isAdmin = trainer?.is_admin;
  
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
    otp: ""
  });
  
  const [notifications, setNotifications] = useState({
    email_updates: true,
    client_reminders: true,
    marketing_emails: false
  });

  const handleSendOtp = async () => {
    setOtpLoading(true);
    try {
      const res = await api.post('/auth/send-password-otp');
      toast.success(res.data.message || "OTP sent to your email!");
      setOtpSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current_password || !passwordForm.new_password) {
      toast.error("Please fill in all password fields");
      return;
    }

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }

    if (passwordForm.new_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (isAdmin && !passwordForm.otp) {
      toast.error("Please enter the OTP sent to your email");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      };
      if (isAdmin) payload.otp = passwordForm.otp;

      await api.post('/auth/change-password', payload);
      toast.success("Password changed successfully!");
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "", otp: "" });
      setOtpSent(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotifications = async () => {
    setLoading(true);
    try {
      await api.put('/settings/notifications', notifications);
      toast.success("Notification settings saved!");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pb-8">
      <Header title="Settings" subtitle="Manage your account" />

      <main className="container mx-auto px-4 py-6 max-w-2xl space-y-6">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="text-muted-foreground hover:text-foreground"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {/* Change Password */}
        <Card className="border-white/[0.06] bg-card/95">
          <CardHeader>
            <CardTitle className="font-heading text-lg uppercase tracking-wide flex items-center gap-2">
              <Lock className="w-5 h-5 text-primary" />
              Change Password
            </CardTitle>
            <CardDescription>
              Update your account password
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current_password">Current Password</Label>
              <Input
                id="current_password"
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))}
                placeholder="Enter current password"
                className="bg-white/[0.03] border-white/[0.06]"
                data-testid="current-password-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="new_password">New Password</Label>
              <Input
                id="new_password"
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
                placeholder="Enter new password"
                className="bg-white/[0.03] border-white/[0.06]"
                data-testid="new-password-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm New Password</Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                placeholder="Confirm new password"
                className="bg-white/[0.03] border-white/[0.06]"
                data-testid="confirm-password-input"
              />
            </div>

            {isAdmin && (
              <div className="space-y-3 p-4 rounded-xl bg-primary/5 border border-primary/20">
                <div className="flex items-center gap-2 text-sm text-primary">
                  <Shield className="w-4 h-4" />
                  <span className="font-semibold uppercase tracking-wide">Admin Verification Required</span>
                </div>
                {!otpSent ? (
                  <Button
                    onClick={handleSendOtp}
                    disabled={otpLoading}
                    variant="outline"
                    className="w-full h-10 border-primary/30 text-primary hover:bg-primary/10 font-semibold tracking-wide uppercase text-xs rounded-xl"
                    data-testid="send-otp-btn"
                  >
                    {otpLoading ? <div className="spinner-sm" /> : (
                      <>
                        <KeyRound className="w-4 h-4 mr-2" />
                        Send OTP to Email
                      </>
                    )}
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="otp">Enter OTP</Label>
                    <Input
                      id="otp"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={passwordForm.otp}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, otp: e.target.value.replace(/\D/g, '') }))}
                      placeholder="6-digit OTP"
                      className="bg-white/[0.03] border-primary/30 text-center text-lg tracking-[0.5em] font-mono"
                      data-testid="otp-input"
                    />
                    <button
                      onClick={handleSendOtp}
                      disabled={otpLoading}
                      className="text-xs text-primary hover:underline"
                      data-testid="resend-otp-btn"
                    >
                      {otpLoading ? "Sending..." : "Resend OTP"}
                    </button>
                  </div>
                )}
              </div>
            )}

            <Button
              onClick={handleChangePassword}
              disabled={loading || (isAdmin && !otpSent)}
              className="w-full h-11 font-semibold tracking-wide uppercase rounded-xl"
              data-testid="change-password-btn"
            >
              {loading ? <div className="spinner-sm" /> : "Change Password"}
            </Button>
          </CardContent>
        </Card>

        {/* Email Preferences */}
        <Card className="border-white/[0.06] bg-card/95">
          <CardHeader>
            <CardTitle className="font-heading text-lg uppercase tracking-wide flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              Email Preferences
            </CardTitle>
            <CardDescription>
              Manage your email notifications
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium text-sm">Account Updates</p>
                <p className="text-xs text-muted-foreground">Receive important account notifications</p>
              </div>
              <Switch
                checked={notifications.email_updates}
                onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, email_updates: checked }))}
                data-testid="email-updates-switch"
              />
            </div>
            
            <div className="flex items-center justify-between py-2 border-t border-border/30">
              <div>
                <p className="font-medium text-sm">Client Reminders</p>
                <p className="text-xs text-muted-foreground">Get notified about client updates</p>
              </div>
              <Switch
                checked={notifications.client_reminders}
                onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, client_reminders: checked }))}
                data-testid="client-reminders-switch"
              />
            </div>
            
            <div className="flex items-center justify-between py-2 border-t border-border/30">
              <div>
                <p className="font-medium text-sm">Marketing Emails</p>
                <p className="text-xs text-muted-foreground">Receive tips, news, and promotions</p>
              </div>
              <Switch
                checked={notifications.marketing_emails}
                onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, marketing_emails: checked }))}
                data-testid="marketing-emails-switch"
              />
            </div>

            <Button
              onClick={handleSaveNotifications}
              disabled={loading}
              variant="outline"
              className="w-full h-11 font-semibold tracking-wide uppercase rounded-xl border-primary/50 text-primary hover:bg-primary/10"
              data-testid="save-notifications-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Preferences
            </Button>
          </CardContent>
        </Card>

        {/* Support */}
        <Card className="border-white/[0.06] bg-card/95">
          <CardHeader>
            <CardTitle className="font-heading text-lg uppercase tracking-wide flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              Support
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Need help? Contact our support team.
            </p>
            <a 
              href="mailto:support@fittrackcoach.in"
              className="inline-flex items-center gap-2 text-primary hover:underline"
              data-testid="support-email-link"
            >
              <Mail className="w-4 h-4" />
              support@fittrackcoach.in
            </a>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
